import React, { useState, useMemo, useRef } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import api from '../../api/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import fullLogo from '../../assets/full_logo.png';
import { toastError } from '../../utils/toasts';
import { Icon, Button } from '../ui';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const CATEGORY_OPTIONS = [
  { value: '', label: 'Toutes' },
  { value: 'accounting', label: 'Comptabilité' },
  { value: 'fiscal', label: 'Fiscal' },
  { value: 'social', label: 'Social' },
  { value: 'legal', label: 'Juridique' },
  { value: 'other', label: 'Autre' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Tous statuts' },
  { value: 'pending', label: 'En attente' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'completed', label: 'Terminée' },
  { value: 'overdue', label: 'En retard' },
];

const TaskReport = ({ tasks = [], clients = [], collaborators = [] }) => {
  const componentRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);

  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTaskName, setFilterTaskName] = useState('');
  
  const [cabinetName, setCabinetName] = useState('CABINET');

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/settings');
        const data = res.data || {};
        const name = data.companyName || data.name || 'CABINET';
        if (mounted) setCabinetName(name);
      } catch (e) {
        // on garde CABINET
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filteredData = useMemo(() => {
    return tasks.filter((t) => {
      const tDate = new Date(t.dueDate || t.updatedAt || Date.now());
      tDate.setHours(0, 0, 0, 0);

      if (filterDateStart) {
        const dStart = new Date(filterDateStart);
        dStart.setHours(0, 0, 0, 0);
        if (tDate < dStart) return false;
      }

      if (filterDateEnd) {
        const dEnd = new Date(filterDateEnd);
        dEnd.setHours(0, 0, 0, 0);
        if (tDate > dEnd) return false;
      }

      if (filterClient) {
        const cId = (t.clientId?._id || t.clientId || '').toString();
        if (cId !== String(filterClient)) return false;
      }

      if (filterCategory && t.category !== filterCategory) return false;

      if (filterUser) {
        const uId = (t.assignedTo?._id || t.assignedTo || '').toString();
        if (uId !== String(filterUser)) return false;
      }

      if (filterStatus && t.status !== filterStatus) return false;

      if (filterTaskName) {
        const taskTitle = (t.title || '').toLowerCase();
        const searchQuery = filterTaskName.toLowerCase().trim();
        if (!taskTitle.includes(searchQuery)) return false;
      }

      return true;
    });
  }, [tasks, filterDateStart, filterDateEnd, filterClient, filterCategory, filterUser, filterStatus, filterTaskName]);

  const stats = useMemo(() => {
    const data = {
      total: filteredData.length,
      completed: 0,
      overdue: 0,
      hoursLogged: 0,
      hoursEstimated: 0,
      byStatus: { pending: 0, in_progress: 0, completed: 0, overdue: 0 },
      byUser: {},
      byClient: {},
    };

    const now = new Date();

    filteredData.forEach((t) => {
      const status = t.status || 'pending';
      data.byStatus[status] = (data.byStatus[status] || 0) + 1;

      if (status === 'completed') data.completed++;

      if (status !== 'completed' && (status === 'overdue' || (t.dueDate && new Date(t.dueDate) < now))) {
        data.overdue++;
      }

      data.hoursEstimated += Number(t.estimatedHours || 0);

      // ✅ CORRECTION : actualHours contient déjà le total de tous les timers
      // (calculé par le backend lors du stop)
      const totalTaskHours = Number(t.actualHours || 0);
      data.hoursLogged += totalTaskHours;

      const uName = t.assignedTo?.name || t.assignedTo?.email || 'Non assigné';
      if (!data.byUser[uName]) data.byUser[uName] = { count: 0, hours: 0 };
      data.byUser[uName].count++;
      data.byUser[uName].hours += totalTaskHours;

      const cName = t.clientId?.companyName || t.clientId?.name || 'Divers';
      if (!data.byClient[cName]) data.byClient[cName] = { count: 0, completed: 0, hours: 0 };
      data.byClient[cName].count++;
      data.byClient[cName].hours += totalTaskHours;
      if (status === 'completed') data.byClient[cName].completed++;
    });

    return data;
  }, [filteredData]);

  const barData = useMemo(
    () => ({
      labels: Object.keys(stats.byUser),
      datasets: [
        {
          label: 'Tâches',
          data: Object.values(stats.byUser).map((u) => u.count),
          backgroundColor: 'rgba(79, 70, 229, 0.25)',
          borderColor: 'rgba(79, 70, 229, 1)',
          borderWidth: 1,
          borderRadius: 8,
        },
        {
          label: 'Heures (h)',
          data: Object.values(stats.byUser).map((u) => Number(u.hours.toFixed(1))),
          backgroundColor: 'rgba(16, 185, 129, 0.25)',
          borderColor: 'rgba(16, 185, 129, 1)',
          borderWidth: 1,
          borderRadius: 8,
        },
      ],
    }),
    [stats.byUser]
  );

  const doughnutData = useMemo(
    () => ({
      labels: ['En attente', 'En cours', 'Terminé', 'En retard'],
      datasets: [
        {
          data: [
            stats.byStatus.pending,
            stats.byStatus.in_progress,
            stats.byStatus.completed,
            stats.overdue,
          ],
          backgroundColor: [
            'rgba(251, 191, 36, 0.6)',
            'rgba(59, 130, 246, 0.6)',
            'rgba(16, 185, 129, 0.6)',
            'rgba(239, 68, 68, 0.6)',
          ],
          borderColor: [
            'rgba(251, 191, 36, 1)',
            'rgba(59, 130, 246, 1)',
            'rgba(16, 185, 129, 1)',
            'rgba(239, 68, 68, 1)',
          ],
          borderWidth: 1,
        },
      ],
    }),
    [stats]
  );

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#111827',
            boxWidth: 12,
            boxHeight: 12,
          },
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        x: {
          ticks: { color: '#374151' },
          grid: { color: '#E5E7EB' },
        },
        y: {
          ticks: { color: '#374151' },
          grid: { color: '#E5E7EB' },
        },
      },
    }),
    []
  );

  const doughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      cutout: '65%',
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#111827',
            boxWidth: 12,
            boxHeight: 12,
          },
        },
        tooltip: {
          enabled: true,
        },
      },
    }),
    []
  );

  const handleResetFilters = () => {
    setFilterDateStart('');
    setFilterDateEnd('');
    setFilterClient('');
    setFilterCategory('');
    setFilterUser('');
    setFilterStatus('');
    setFilterTaskName('');
  };

  const handleExportPDF = async () => {
    try {
      if (!componentRef.current) {
        toastError("Zone du rapport introuvable.");
        return;
      }

      setIsExporting(true);

      await new Promise((r) => setTimeout(r, 300));

      const canvas = await html2canvas(componentRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        scrollY: -window.scrollY,
      });

      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `Rapport_Taches_${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.pdf`;
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank');
    } catch (e) {
      console.error('Erreur export PDF:', e);
      toastError("Erreur lors de la génération du PDF : " + (e.message || 'inconnue'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = () => {
    const headers = ['Titre', 'Client', 'Assigné à', 'Statut', 'Date échéance', 'Début', 'Fin', 'Heures Est.', 'Heures Réelles'];
    const rows = filteredData.map((t) => {
      const calcHours = (t) => {
        const h = Number(t.actualHours || 0);
        if (h > 0) return h;
        if (t.startedAt && t.completedAt) {
          const diff = new Date(t.completedAt) - new Date(t.startedAt);
          return diff / (1000 * 60 * 60);
        }
        return 0;
      };

      const realHours = calcHours(t);

      const statusLabel =
        t.status === 'in_progress'
          ? 'En cours'
          : t.status === 'pending'
          ? 'En attente'
          : t.status === 'completed'
          ? 'Terminée'
          : 'En retard';

      return [
        `"${String(t.title || '').replace(/"/g, '""')}"`,
        `"${String(t.clientId?.companyName || 'N/A').replace(/"/g, '""')}"`,
        `"${String(t.assignedTo?.name || t.assignedTo?.email || 'Non assigné').replace(/"/g, '""')}"`,
        statusLabel,
        t.dueDate ? new Date(t.dueDate).toLocaleDateString('fr-FR') : '',
        t.startedAt ? new Date(t.startedAt).toLocaleString('fr-FR') : '',
        t.completedAt ? new Date(t.completedAt).toLocaleString('fr-FR') : '',
        formatHours(t.estimatedHours),
        formatHours(realHours),
      ];
    });

    const csvContent = '\uFEFF' + headers.join(';') + '\n' + rows.map((e) => e.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Rapport_Taches_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatHours = (h) => {
    if (h === undefined || h === null || isNaN(h)) return '';
    const hours = Math.floor(h);
    const mins = Math.round((h - hours) * 60);
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  return (
    <div className="space-y-6">
      {/* Barre d'outils avec filtres */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-end gap-3">
          {/* Filtre par Nom de tâche */}
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-bold text-gray-500 mb-1">
              <Icon name="IconSearch" className="mr-1" /> Recherche par nom
            </label>
            <input
              type="text"
              placeholder="Nom de la tâche..."
              className="border rounded px-3 py-2 text-sm w-full"
              value={filterTaskName}
              onChange={(e) => setFilterTaskName(e.target.value)}
            />
          </div>

          {/* Date début */}
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Du</label>
            <input
              type="date"
              className="border rounded px-2 py-2 text-sm"
              value={filterDateStart}
              onChange={(e) => setFilterDateStart(e.target.value)}
            />
          </div>

          {/* Date fin */}
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Au</label>
            <input
              type="date"
              className="border rounded px-2 py-2 text-sm"
              value={filterDateEnd}
              onChange={(e) => setFilterDateEnd(e.target.value)}
            />
          </div>

          {/* Client */}
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Client</label>
            <select
              className="border rounded px-2 py-2 text-sm w-40"
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
            >
              <option value="">Tous</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.companyName}
                </option>
              ))}
            </select>
          </div>

          {/* Collaborateur */}
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Collaborateur</label>
            <select
              className="border rounded px-2 py-2 text-sm w-44"
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
            >
              <option value="">Tous</option>
              {collaborators.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name || u.email}
                </option>
              ))}
            </select>
          </div>

          {/* Catégorie */}
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Catégorie</label>
            <select
              className="border rounded px-2 py-2 text-sm w-36"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Filtre par Statut */}
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Statut</label>
            <select
              className="border rounded px-2 py-2 text-sm w-40"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Bouton Réinitialiser */}
          <div>
            <button
              onClick={handleResetFilters}
              className="px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded border border-red-200 transition-all"
              title="Réinitialiser tous les filtres"
            >
              <Icon name="IconRefresh" className="mr-1" /> Réinitialiser
            </button>
          </div>

          <div className="flex-grow"></div>

          {/* Boutons d'export */}
          <div className="flex gap-2">
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-2 shadow transition-all disabled:opacity-60"
            >
              <Icon name="IconFilePdf" /> {isExporting ? 'Génération...' : 'Exporter PDF'}
            </button>

            <button
              onClick={handleExportExcel}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2 shadow transition-all"
            >
              <Icon name="IconFileSpreadsheet" /> Export Excel
            </button>
          </div>
        </div>

        {/* Indicateur de filtres actifs */}
        {(filterTaskName || filterStatus || filterClient || filterCategory || filterUser || filterDateStart || filterDateEnd) && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs font-semibold text-gray-600">Filtres actifs:</span>
              
              {filterTaskName && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                  <Icon name="IconSearch" /> "{filterTaskName}"
                  <button onClick={() => setFilterTaskName('')} className="ml-1 hover:text-blue-900">×</button>
                </span>
              )}
              
              {filterStatus && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                  Statut: {STATUS_OPTIONS.find(s => s.value === filterStatus)?.label}
                  <button onClick={() => setFilterStatus('')} className="ml-1 hover:text-purple-900">×</button>
                </span>
              )}
              
              {filterClient && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                  Client: {clients.find(c => c._id === filterClient)?.companyName}
                  <button onClick={() => setFilterClient('')} className="ml-1 hover:text-green-900">×</button>
                </span>
              )}
              
              {filterCategory && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                  Catégorie: {CATEGORY_OPTIONS.find(c => c.value === filterCategory)?.label}
                  <button onClick={() => setFilterCategory('')} className="ml-1 hover:text-orange-900">×</button>
                </span>
              )}
              
              {filterUser && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">
                  Collaborateur: {collaborators.find(u => u._id === filterUser)?.name}
                  <button onClick={() => setFilterUser('')} className="ml-1 hover:text-indigo-900">×</button>
                </span>
              )}
              
              {(filterDateStart || filterDateEnd) && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">
                  <Icon name="IconCalendar" /> 
                  {filterDateStart && new Date(filterDateStart).toLocaleDateString('fr-FR')} 
                  {filterDateStart && filterDateEnd && ' → '}
                  {filterDateEnd && new Date(filterDateEnd).toLocaleDateString('fr-FR')}
                  <button onClick={() => { setFilterDateStart(''); setFilterDateEnd(''); }} className="ml-1 hover:text-gray-900">×</button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Zone exportable (PDF) */}
      <div ref={componentRef} className="bg-white p-8 w-full">
        {/* En-tête */}
        <div className="pdf-only grid grid-cols-3 items-center mb-8 border-b pb-4">
          <div className="flex items-center gap-3">
            <img
              src={fullLogo}
              alt="AccountInTime"
              className="h-12 w-auto object-contain"
              onError={(e) => (e.target.style.display = 'none')}
            />
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-indigo-900">{cabinetName}</div>
          </div>

          <div className="text-right">
            <h1 className="text-xl font-bold text-gray-800">Rapport d'Activité</h1>
            <p className="text-sm text-gray-500">Généré le {new Date().toLocaleDateString('fr-FR')}</p>
            <p className="text-xs text-gray-400">
              Période : {filterDateStart ? new Date(filterDateStart).toLocaleDateString('fr-FR') : 'Début'} - {filterDateEnd ? new Date(filterDateEnd).toLocaleDateString('fr-FR') : 'Fin'}
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-center">
            <p className="text-gray-500 text-xs uppercase font-bold">Total Tâches</p>
            <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-center">
            <p className="text-gray-500 text-xs uppercase font-bold">Heures Réelles</p>
            <p className="text-3xl font-bold text-blue-600">{stats.hoursLogged.toFixed(1)}h</p>
            <p className="text-xs text-gray-400">Est. {stats.hoursEstimated}h</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-center">
            <p className="text-gray-500 text-xs uppercase font-bold">Terminées</p>
            <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-center">
            <p className="text-gray-500 text-xs uppercase font-bold">Retard</p>
            <p className="text-3xl font-bold text-red-600">{stats.overdue}</p>
          </div>
        </div>

        {/* Graphiques */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 bg-white p-4 border border-gray-200 rounded-xl">
            <h3 className="font-bold text-gray-700 mb-4">Performance Collaborateurs</h3>
            <div className="h-64">
              <Bar data={barData} options={barOptions} />
            </div>
          </div>
          <div className="bg-white p-4 border border-gray-200 rounded-xl flex flex-col items-center">
            <h3 className="font-bold text-gray-700 mb-4">Répartition Statuts</h3>
            <div className="h-48 w-48">
              <Doughnut data={doughnutData} options={doughnutOptions} />
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-100 text-gray-700 font-bold uppercase text-xs">
              <tr>
                <th className="px-6 py-3 border-b">Tâche</th>
                <th className="px-6 py-3 border-b">Client</th>
                <th className="px-6 py-3 border-b">Collaborateur</th>
                <th className="px-6 py-3 border-b">Catégorie</th>
                <th className="px-6 py-3 text-center border-b">Statut</th>
                <th className="px-6 py-3 text-center border-b">Échéance</th>
                <th className="px-6 py-3 text-center border-b">Début</th>
                <th className="px-6 py-3 text-center border-b">Fin</th>
                <th className="px-6 py-3 text-center border-b">Heures</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {filteredData.map((t) => {
                const clientName = t.clientId?.companyName || t.clientId?.name || 'N/A';
                const collabName = t.assignedTo?.name || t.assignedTo?.email || 'Non assigné';
                const category = t.category || 'other';

                // ✅ CORRECTION : utiliser actualHours directement
                const totalHours = (() => {
                  const h = Number(t.actualHours || 0);
                  if (h > 0) return h;
                  if (t.startedAt && t.completedAt) {
                    const diff = new Date(t.completedAt) - new Date(t.startedAt);
                    return diff / (1000 * 60 * 60);
                  }
                  return 0;
                })();

                const statusLabel =
                  t.status === 'in_progress'
                    ? 'En cours'
                    : t.status === 'pending'
                    ? 'En attente'
                    : t.status === 'completed'
                    ? 'Terminée'
                    : 'En retard';

                return (
                  <tr key={t._id || `${t.title}-${t.dueDate || ''}`}>
                    <td className="px-6 py-3 font-medium text-gray-800">
                      {t.title || ''}
                    </td>
                    <td className="px-6 py-3">{clientName}</td>
                    <td className="px-6 py-3">{collabName}</td>
                    <td className="px-6 py-3">{category}</td>
                    <td className="px-6 py-3 text-center">{statusLabel}</td>
                    <td className="px-6 py-3 text-center">
                      {t.dueDate ? new Date(t.dueDate).toLocaleDateString('fr-FR') : ''}
                    </td>
                    <td className="px-6 py-3 text-center text-xs">
                      {t.startedAt ? new Date(t.startedAt).toLocaleString('fr-FR') : ''}
                    </td>
                    <td className="px-6 py-3 text-center text-xs">
                      {t.completedAt ? new Date(t.completedAt).toLocaleString('fr-FR') : ''}
                    </td>
                    <td className="px-6 py-3 text-center text-blue-700 font-semibold">
                      {formatHours(totalHours)}
                    </td>
                  </tr>
                );
              })}

              {filteredData.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-gray-500">
                    <Icon name="IconInbox" size={36} className="mb-2 block text-gray-300 mx-auto" />
                    Aucune tâche ne correspond aux filtres sélectionnés.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-8 text-center text-xs text-gray-400 border-t pt-4">
          <p>Document généré par AccountInTime - © {new Date().getFullYear()}</p>
        </div>
      </div>
    </div>
  );
};

export default TaskReport;